import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useGetDevice from "../product/hooks/useGetDevice";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import { Box, IconButton } from "@mui/material";
// import { DataGrid } from "@mui/x-data-grid";  // <-- replaced by card UI
import {
  PencilSquareIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import {
  getAllSessionExpenses,
  getExpenseDetailsById,
  updateSessionExpense,
} from "../../../config/Services";
import Spinner from "../../../components/Spinner";
import AddExpenseModal from "./components/AddExpenseModal";
import ExpenseHeader from "./components/expenseHeader";
import "./expenseListing.css";
import { getStoreCurrencySymbol } from "../../../utils/currency";

const ExpensesListing = ({ onClose }) => {
  const navigate = useNavigate();
  const isMobile = useGetDevice();
  const showSnackbar = useSnackbar();
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [rowDetails, setRowDetails] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    expenseTitle: "",
    amount: "",
    reason: "",
    ledger: {},
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [updateLoader, setUpdateLoader] = useState(false);
  const [cardLoadingId, setCardLoadingId] = useState(null);

  // ── NEW: search bar state ───────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");

  const licenseId = localStorage.getItem("licenseId");
  const sessionId = localStorage.getItem("sessionId");
  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  let retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));

  const [pageState, setPageState] = useState({
    loader: false,
    dataRows: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  // ── Helper: format numbers with commas ──────────────────────────────────────
  const formatNumberWithCommas = (number) => {
    if (!number && number !== 0) return "0";
    return Number(number).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ── Helper: parse amount from formatted string ──────────────────────────────
  const parseAmountFromString = (amountString) => {
    if (!amountString) return "";
    const numericValue = amountString.replace(/[^\d.-]/g, "");
    return numericValue;
  };

  // ── Helper: format date for card display ───────────────────────────────────
  const formatCardDate = (dateString) => {
    if (!dateString) return { mmdd: "—", time: "—", ampm: "" };
    try {
      const d = new Date(dateString);
      const mmdd = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(
        2,
        "0"
      )}`;
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const time = `${String(hours).padStart(2, "0")}:${minutes}`;
      return { mmdd, time, ampm };
    } catch {
      return { mmdd: "—", time: "—", ampm: "" };
    }
  };

  // ── Status badge component ─────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const s = (status || "").toUpperCase();
    if (s === "APPROVED") {
      return (
        <span className="expense-status-badge expense-status-approved">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-circle-check"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
          Approved
        </span>
      );
    }
    if (s === "REJECTED") {
      return (
        <span className="expense-status-badge expense-status-rejected">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-circle-alert"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" x2="12" y1="8" y2="12"></line>
            <line x1="12" x2="12.01" y1="16" y2="16"></line>
          </svg>
          Rejected
        </span>
      );
    }
    // default → pending
    return (
      <span className="expense-status-badge expense-status-pending">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-clock"
          aria-hidden="true"
        >
          <path d="M12 6v6l4 2"></path>
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
        Pending
      </span>
    );
  };

  // ── handleEdit (logic unchanged) ───────────────────────────────────────────
  const handleEdit = async (row) => {
    setRowDetails(row);
    setCardLoadingId(row?.id); // ✅ start loader for this card

    try {
      const res = await getExpenseDetailsById(row?.id);

      if (res?.data?.status === "ERROR" || res?.data?.data?.es > 0) {
        showSnackbar(res?.data?.data?.message || "Error fetching expense details", "error");
        return;
      }

      const expenseData = res?.data?.data?.data?.[0];

      if (!expenseData) {
        showSnackbar("No expense data found", "error");
        return;
      }

      const paidThroughEntry = res?.data?.data?.data?.find(
        (item) => item?.entryType === "PAID_THROUGH"
      );

      const formData = {
        expenseTitle: expenseData?.voucherEntryName || "",
        amount: parseAmountFromString(expenseData?.amount?.toString()) || "",
        reason: expenseData?.description || "",
        ledger: expenseData?.ledgerId
          ? {
              label: expenseData?.ledgerName || expenseData?.voucherEntryName,
              value: expenseData?.ledgerId,
            }
          : {},
        voucherEntryId: expenseData?.voucherEntryId,
        voucherReferenceId: expenseData?.voucherReferenceId,
        data: expenseData,
        paidThroughVoucherId: paidThroughEntry?.voucherEntryId || null,
      };

      setEditFormData(formData);

      const parsedDocuments = expenseData?.document ? JSON.parse(expenseData?.document) : [];
      const firstDoc = parsedDocuments?.[0] || {};
      const [fileName, fileUrl] = Object.entries(firstDoc)?.[0] || ["", ""];

      setUploadedFile({
        name: fileName,
        url: fileUrl,
      });

      setOpenEditModal(true);
    } catch (error) {
      showSnackbar(error?.message || "Error loading expense details", "error");
    } finally {
      setCardLoadingId(null); // ✅ stop loader
    }
  };

  // ── handleCloseEdit (logic unchanged) ─────────────────────────────────────
  const handleCloseEdit = () => {
    setOpenEditModal(false);
    setRowDetails(null);
    setEditFormData({
      expenseTitle: "",
      amount: "",
      reason: "",
      ledger: {},
    });
    setUploadedFile(null);
  };

  // ── validateUpdatePayload (logic unchanged) ────────────────────────────────
  const validateUpdatePayload = (payload) => {
    if (!payload?.organizationId) {
      showSnackbar("Select Company", "warning");
      return false;
    }
    if (!payload?.locationId) {
      showSnackbar("Select Location", "warning");
      return false;
    }
    if (!payload?.voucherEntryName) {
      showSnackbar("Enter expense title", "warning");
      return false;
    }
    if (!editFormData?.amount) {
      showSnackbar("Please enter amount", "warning");
      return false;
    }
    if (!editFormData?.ledger?.value) {
      showSnackbar("Please select expense ledger", "warning");
      return false;
    }
    return true;
  };

  // ── handleUpdateExpense (logic unchanged) ──────────────────────────────────
  const handleUpdateExpense = async () => {
    setUpdateLoader(true);

    const payload = {
      locationId: locId || "",
      organizationId: orgId || "",
      voucherDate: editFormData?.data?.voucherDate || format(new Date(), "yyyy-MM-dd"),
      voucherMasterId: "VM0000007",
      voucherEntryName: editFormData?.expenseTitle,
      isRecurring: editFormData?.data?.isRecurring || false,
      recurringFrequency: "NONE",
      currencyId: "CUR000002",
      description: editFormData?.reason || "",
      voucherParticularRequests: [
        {
          ledgerId: editFormData?.ledger?.value,
          isDebit: true,
          entryType: "EXPENSE",
          amount: Number(editFormData?.amount) || 0,
          voucherEntryId: editFormData?.voucherEntryId,
        },
        {
          ledgerId: "LED000046",
          masterBankFacilityId: null,
          isDebit: false,
          entryType: "PAID_THROUGH",
          amount: Number(editFormData?.amount) || 0,
          voucherEntryId: editFormData?.paidThroughVoucherId,
        },
      ],
      properties: [],
      documents: uploadedFile
        ? [
            {
              [uploadedFile.name]: uploadedFile.url || "",
            },
          ]
        : rowDetails?.rawData?.documents || [],
      createdBy: retailUserLocalStorageData?.uidx,
      createdByName: retailUserLocalStorageData?.firstName,
      isRecurrenceEndless: false,
      recurrenceStartDate: "",
      recurrenceEndDate: null,
      isNotify: false,
      notificationDate: 0,
      plusmaxReferenceId: 0,
      billDueDate: null,
      billCreationDate: null,
      isQualifiedTax: false,
      mappedType: "NONE",
      isBillable: false,
      vendorName: null,
      plusmaxReferenceName: "",
      voucherReferenceId: rowDetails?.referenceId || rowDetails?.id,
    };

    const isValidated = validateUpdatePayload(payload);
    if (!isValidated) {
      setUpdateLoader(false);
      return;
    }

    updateSessionExpense(payload)
      .then((res) => {
        if (res?.data?.data?.es === 1) {
          showSnackbar(res?.data?.data?.message || "Error while updating expense", "error");
        } else if (res?.data?.status === "ERROR") {
          showSnackbar("Error while updating expense", "error");
        } else {
          showSnackbar("Expense Updated Successfully", "success");
          // Refresh the data
          getAllCreatedVoucherEntries(pageState.page - 1);
          handleCloseEdit();
        }
        setUpdateLoader(false);
      })
      .catch((err) => {
        showSnackbar(err?.message || "Error while updating expense", "error");
        setUpdateLoader(false);
      });
  };

  // ── Custom event listener (logic unchanged) ────────────────────────────────
  useEffect(() => {
    const handleEvent = (event) => {
      // Check event.detail.type instead of event.type
      if (event.detail?.type === "expenseAdded") {
        getAllCreatedVoucherEntries(pageState.page - 1);
      }
    };

    window.addEventListener("customEvent", handleEvent);

    return () => {
      window.removeEventListener("customEvent", handleEvent);
    };
  }, [pageState.page]);

  // ── getAllCreatedVoucherEntries (logic unchanged) ───────────────────────────
  const getAllCreatedVoucherEntries = (pageIndex = 0) => {
    setPageState((old) => ({ ...old, loader: true }));
    setLoading(true);

    const pageSize = pageState.pageSize;

    getAllSessionExpenses(orgId, locId, pageIndex, pageSize, sessionId)
      .then((res) => {
        setPageState((old) => ({ ...old, loader: false }));
        setLoading(false);

        if (res?.data?.status === "SUCCESS" && res?.data?.data?.es === 0) {
          const expenses = res?.data?.data?.data?.data || [];
          const totalResults = res?.data?.data?.data?.totalResult || 0;

          if (expenses?.length === 0) {
            setPageState((old) => ({ ...old, dataRows: [], total: 0 }));
            setNoData(true);
            return;
          }

          // Map the API response to match the DataGrid columns
          const formattedExpenses = expenses.map((expense, idx) => ({
            id: expense?.id || expense?.voucherReferenceId || idx,
            voucherNo: expense?.voucherNo || expense?.voucherReferenceId || "NA",
            sessionId: expense?.sessionId || sessionId || "NA",
            expenseTitle:
              expense?.expenseTitle || expense?.voucherEntryName || expense?.ledgerName || "NA",
            expenseReason: expense?.description || "NA",
            amount: expense?.amount
              ? `${expense?.isoCurrencyCode || ""} ${formatNumberWithCommas(expense?.amount)}`
              : expense?.voucherValue
              ? formatNumberWithCommas(expense?.voucherValue)
              : "NA",
            // Store additional data for edit functionality
            voucherDate: expense?.voucherDate,
            ledger: expense?.ledgerName,
            isRecurring: expense?.isRecurring,
            status: expense?.status,
            referenceId: expense?.voucherReferenceId,
            createdByName: expense?.createdByName || expense?.createdBy || null,
            approvedByName: expense?.approvedByName || null,
            rawData: expense,
          }));

          setPageState((old) => ({ ...old, dataRows: formattedExpenses, total: totalResults }));
          setNoData(false);
        } else {
          setPageState((old) => ({ ...old, dataRows: [], total: 0 }));
          setNoData(true);
          showSnackbar(
            res?.data?.data?.message || res?.data?.message || "No expenses found",
            "error"
          );
        }
      })
      .catch((err) => {
        setPageState((old) => ({ ...old, loader: false }));
        setLoading(false);
        setPageState((old) => ({ ...old, dataRows: [], total: 0 }));
        setNoData(true);
        showSnackbar(err?.message || "Error fetching expenses", "error");
      });
  };

  useEffect(() => {
    if (orgId && locId && sessionId) {
      getAllCreatedVoucherEntries(pageState.page - 1);
    } else {
      showSnackbar("Missing required information. Please check your session.", "error");
    }
  }, [pageState.page]);

  // ── Pagination helpers ─────────────────────────────────────────────────────
  const totalPages = Math.ceil(pageState?.total / pageState?.pageSize) || 1;

  const handlePrevPage = () => {
    if (pageState?.page > 1) setPageState((old) => ({ ...old, page: old.page - 1 }));
  };

  const handleNextPage = () => {
    if (pageState?.page < totalPages) setPageState((old) => ({ ...old, page: old.page + 1 }));
  };

  // ── NEW: filter currently-loaded rows by search term ────────────────────────
  const filteredRows =
    pageState?.dataRows?.filter((row) => {
      if (!searchTerm?.trim()) return true;
      const term = searchTerm?.toLowerCase();
      return (
        row?.expenseTitle?.toLowerCase().includes(term) ||
        row?.expenseReason?.toLowerCase().includes(term) ||
        row?.ledger?.toLowerCase().includes(term)
      );
    }) || [];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Box className="expenses-page-wrapper">
        {/* ── Panel header ── */}
        <div className="expenses-panel-header">
          <h1 className="expenses-panel-title-new">Expenses</h1>
          <div className="expenses-panel-header-right-new">
            {/* ── NEW: search bar ── */}
            <div className="expenses-search-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="expenses-search-icon"
                aria-hidden="true"
              >
                <path d="m21 21-4.34-4.34"></path>
                <circle cx="11" cy="11" r="8"></circle>
              </svg>
              <input
                type="text"
                className="expenses-search-input"
                placeholder="Search expenses..."
                data-testid="input-expenses-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <ExpenseHeader />
            <button
              className="expenses-close-btn"
              onClick={() => (onClose ? onClose() : navigate(-1))}
              data-testid="button-expenses-close"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="expenses-close-icon"
                aria-hidden="true"
              >
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Card list ── */}
        {loading || pageState?.loader ? (
          <Box
            sx={{
              height: "420px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spinner />
          </Box>
        ) : noData || pageState?.dataRows?.length === 0 ? (
          <div className="expenses-empty-state">
            <BanknotesIcon />
            <p>No expenses found for this session.</p>
          </div>
        ) : (
          <>
            <div className="expenses-list-area">
              {filteredRows?.length === 0 ? (
                <div className="expenses-empty-state">
                  <BanknotesIcon />
                  <p>No expenses match your search.</p>
                </div>
              ) : (
                filteredRows?.map((row) => {
                  const { mmdd, time, ampm } = formatCardDate(row?.voucherDate);
                  return (
                    <div key={row?.id} className="expense-card" onClick={() => handleEdit(row)}>
                      <div
                        className={`expense-card-loader ${
                          cardLoadingId === row?.id ? "active" : ""
                        }`}
                      >
                        {cardLoadingId === row?.id && <Spinner />}
                      </div>

                      {/* Date column */}
                      <div className="expense-card-date">
                        <span className="expense-card-date-mmdd">{mmdd}</span>
                        <span className="expense-card-date-time">{time}</span>
                        <span className="expense-card-date-ampm">{ampm}</span>
                      </div>

                      {/* Main content */}
                      <div className="expense-card-content">
                        <div className="expense-card-top-row">
                          <span className="expense-card-title">{row?.expenseTitle}</span>
                          {row?.ledger && row.ledger !== "NA" && (
                            <span className="expense-card-badge">{row?.ledger}</span>
                          )}
                        </div>

                        {row?.expenseReason && row?.expenseReason !== "NA" && (
                          <p className="expense-card-reason">{row?.expenseReason}</p>
                        )}

                        <div className="expense-card-meta">
                          <span className="expense-card-meta-item">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-user"
                              aria-hidden="true"
                              style={{ width: "14px", height: "14px" }}
                            >
                              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            <span>Created by {row?.createdByName ?? "NA"}</span>
                          </span>

                          {/* {row?.approvedByName && ( */}
                          <span
                            className={`expense-card-meta-item ${
                              row?.approvedByName
                                ? "expense-card-meta-approved"
                                : "expense-card-meta-not-approved"
                            }`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-circle-check"
                              aria-hidden="true"
                              style={{ width: "14px", height: "14px" }}
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="m9 12 2 2 4-4"></path>
                            </svg>
                            <span>Approved by {row?.approvedByName || "NA"}</span>
                          </span>
                          {/* )} */}
                        </div>
                      </div>

                      {/* Right: amount + status + edit */}
                      <div className="expense-card-right">
                        <span className="expense-card-amount">
                          {row?.amount && row?.amount !== "NA"
                            ? row?.amount.includes(" ") || /[A-Z]/.test(row?.amount?.charAt(0))
                              ? row?.amount
                              : `${getStoreCurrencySymbol()}${row?.amount}`
                            : "NA"}
                        </span>
                        <StatusBadge status={row?.status} />
                        {/* <button
                          className="expense-card-edit-btn"
                          onClick={() => handleEdit(row)}
                          title="Edit expense"
                          type="button"
                        >
                          <PencilSquareIcon />
                        </button> */}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            <div className="expenses-pagination-row">
              <button
                className="expenses-page-btn"
                onClick={handlePrevPage}
                disabled={pageState?.page <= 1}
                type="button"
              >
                <ChevronLeftIcon />
                Prev
              </button>
              <span className="expenses-page-info">
                Page {pageState?.page} of {totalPages}
              </span>
              <button
                className="expenses-page-btn"
                onClick={handleNextPage}
                disabled={pageState?.page >= totalPages}
                type="button"
              >
                Next
                <ChevronRightIcon />
              </button>
            </div>
          </>
        )}
      </Box>

      {/* Edit Expense Modal */}
      <AddExpenseModal
        openNewExpense={openEditModal}
        handleClose={handleCloseEdit}
        uploadedFile={uploadedFile}
        formData={editFormData}
        setFormData={setEditFormData}
        setUploadedFile={setUploadedFile}
        openUploadModal={openUploadModal}
        setOpenUploadModal={setOpenUploadModal}
        handleCreateExpense={handleUpdateExpense}
        loader={updateLoader}
      />
    </>
  );
};

export default ExpensesListing;
